import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useState } from 'react'
import ProgressIndicator from '@/components/dashboard/ProgressIndicator'
import ComplianceCategoryAccordion from '@/components/dashboard/ComplianceCategoryAccordion'
import { complianceAccordionBase } from '@/constants/dashboardConstant'

// ==================== EntityTanAccordion ====================
// A single entity/TAN accordion holding its category-level accordions. Tracks
// its own open state so the first (default-expanded) category loads exactly
// once — on mount when this entity starts open, or on first open otherwise.
const EntityTanAccordion = ({
  entityTan,
  periodRange,
  defaultExpanded,
  onComplianceUpdated,
}) => {
  // ==================== State ====================
  // True once this entity/TAN has been opened at least once. Its first category
  // is default-expanded and therefore never fires its own onChange, so we drive
  // that first category's load from here.
  const [entityOpened, setEntityOpened] = useState(defaultExpanded)

  // ==================== Handlers ====================
  const handleEntityChange = (_event, expanded) => {
    if (expanded) {
      setEntityOpened(true)
    }
  }

  // ==================== Return ====================
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      onChange={handleEntityChange}
      disableGutters
      sx={{
        ...complianceAccordionBase,
        border: '1px solid var(--border-subtel)',
        backgroundColor: 'var(--white-color)',
        mb: 1,
        '&.Mui-expanded': { mb: 1 },
        '&:last-child': { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: 'var(--forbrand-secondary)' }} />
        }
        sx={{
          px: 2,
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
          }}
        >
          {entityTan.entityName} - {entityTan.tanName}
        </Typography>
        <ProgressIndicator
          completed={entityTan.completed}
          total={entityTan.total}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, backgroundColor: 'var(--white-color)' }}>
        {(entityTan.categories || []).map((category, index) => (
          <ComplianceCategoryAccordion
            key={category.categoryCode}
            category={category}
            entityTan={entityTan}
            periodRange={periodRange}
            defaultExpanded={index === 0}
            // Only the first category loads automatically, and only once its
            // entity/TAN has been opened. This keeps initial load to a single
            // rows call (just the first entity's first category).
            autoLoadOnMount={index === 0 && entityOpened}
            onComplianceUpdated={onComplianceUpdated}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

export default EntityTanAccordion
